// 渠道类型定义
export type ChannelType = 'email' | 'wechat_work' | 'feishu' | 'whatsapp';

// 渠道名称映射
export const CHANNEL_LABELS: Record<ChannelType, string> = {
  email: '邮箱',
  wechat_work: '企业微信',
  feishu: '飞书',
  whatsapp: 'WhatsApp',
};

// 渠道颜色映射
export const CHANNEL_COLORS: Record<ChannelType, string> = {
  email: 'blue',
  wechat_work: 'green',
  feishu: 'purple',
  whatsapp: 'cyan',
};

// 渠道内容格式提示
export const CHANNEL_CONTENT_HINTS: Record<ChannelType, string> = {
  email: '正式商务邮件，200-400 词',
  wechat_work: '商务简洁，Markdown 格式，150-300 字',
  feishu: '协作专业，富文本格式，150-300 字',
  whatsapp: '简洁对话式，80-150 词',
};

// 红人洞察报告
export interface InfluencerInsight {
  id: string;
  tenant_id?: string;
  influencer_id: string;
  influencer_url: string;
  product_category?: string;
  analysis_period?: number;
  status: 'analyzing' | 'completed' | 'failed';
  basic_info?: any;
  recent_videos?: any;
  content_themes?: any;
  content_style?: string;
  related_videos?: any;
  collaboration_preference?: any;
  past_cooperation?: any;
  report_content?: any;
  user_annotations?: any;
  created_at: string;
  updated_at?: string;
  created_by?: string;
}

// 发起洞察请求
export interface CreateInsightRequest {
  influencer_id: string;
  influencer_url: string;
  product_category?: string;
  analysis_period?: number;
}

// 批量创建洞察请求
export interface CreateBatchInsightsRequest {
  items: CreateInsightRequest[];
}

// 邀约活动
export interface Campaign {
  id: string;
  tenant_id?: string;
  name: string;
  status: 'draft' | 'ready' | 'sending' | 'paused' | 'completed';
  project_id?: string;
  proposal_id?: string;
  customer_id?: string;
  start_date?: string;
  end_date?: string;
  target_count: number;
  sent_count: number;
  reply_count: number;
  success_count: number;
  notes?: string;
  created_at: string;
  updated_at?: string;
  created_by?: string;
  updated_by?: string;
}

// 创建活动请求
export interface CreateCampaignRequest {
  name: string;
  project_id?: string;
  proposal_id?: string;
  customer_id?: string;
  start_date?: string;
  end_date?: string;
  target_count: number;
  notes?: string;
}

// 更新活动请求
export interface UpdateCampaignRequest {
  name?: string;
  project_id?: string;
  proposal_id?: string;
  customer_id?: string;
  start_date?: string;
  end_date?: string;
  target_count?: number;
  notes?: string;
}

// 邀约消息
export interface OutreachMessage {
  id: string;
  tenant_id?: string;
  campaign_id?: string;
  influencer_id: string;
  insight_id?: string;
  channel: ChannelType;
  subject?: string;
  content: string;
  content_type?: string;
  personalization_data?: any;
  status: 'draft' | 'queued' | 'sent' | 'delivered' | 'read' | 'failed' | 'generating' | 'ready';
  created_at: string;
  updated_at?: string;
  created_by?: string;
  updated_by?: string;
}

// 生成邀约内容请求
export interface GenerateMessageRequest {
  influencer_ids: string[];
  channel: ChannelType;
  tone?: 'professional' | 'friendly' | 'casual';
  product_info: string;
  cooperation_info: string;
}

// 编辑消息请求
export interface UpdateMessageRequest {
  subject?: string;
  content: string;
}

// 发送任务
export interface SendTask {
  id: string;
  tenant_id?: string;
  message_id: string;
  campaign_id?: string;
  influencer_id: string;
  influencer_name?: string;
  channel: string;
  recipient_address: string;
  status: 'queued' | 'scheduled' | 'sending' | 'sent' | 'delivered' | 'read' | 'failed';
  scheduled_at?: string;
  sent_at?: string;
  delivered_at?: string;
  read_at?: string;
  retry_count: number;
  error_message?: string;
  created_at: string;
  updated_at?: string;
  created_by?: string;
}

// 发送进度
export interface SendProgress {
  total: number;
  queued: number;
  scheduled: number;
  sending: number;
  sent: number;
  failed: number;
}

// 批量发送请求
export interface BatchSendRequest {
  recipient_ids: string[];
  scheduled_at?: string;
}

// 发送统计
export interface SendStats {
  campaign_id: string;
  summary?: {
    total: number;
    sent: number;
    delivered: number;
    read: number;
    replied: number;
    failed: number;
  };
  by_status?: {
    queued: number;
    sending: number;
    sent: number;
    delivered: number;
    read: number;
    failed: number;
  };
  reply_analysis?: {
    positive: number;
    neutral: number;
    negative: number;
    follow_up: number;
  };
  timeline?: Array<{
    date: string;
    sent: number;
    replied: number;
  }>;
}

// AI 分析结果
export interface AIAnalysis {
  sentiment: 'positive' | 'neutral' | 'negative' | 'follow_up';
  confidence: number;
  key_info: {
    budget_intent?: string;
    timeline?: string;
    conditions?: string;
    questions?: string[];
  };
  summary: string;
  suggested_action: string;
}

// 回复记录
export interface Reply {
  id: string;
  tenant_id?: string;
  send_task_id: string;
  message_id: string;
  influencer_id: string;
  channel: string;
  reply_content: string;
  received_at: string;
  // 新的字段
  sentiment?: 'positive' | 'neutral' | 'negative' | 'follow_up' | 'unknown';
  ai_analysis?: AIAnalysis;
  is_read: boolean;
  from_email?: string;
  from_name?: string;
  subject?: string;
  content_html?: string;
  // 兼容旧版本
  intent_classification?: 'positive' | 'neutral' | 'negative' | 'follow_up';
  intent_analysis?: string;
  key_concerns?: any;
  suggested_action?: string;
  follow_up_content?: string;
  operator_action?: string;
  operator_notes?: string;
  created_at: string;
  updated_at?: string;
}

// 回复统计
export interface ReplyStats {
  total_replies: number;
  positive_count: number;
  neutral_count: number;
  negative_count: number;
  follow_up_count: number;
  reply_rate: number;
}

// 回复分析响应
export interface ReplyAnalysis {
  reply_id?: string;
  intent_classification?: 'positive' | 'neutral' | 'negative' | 'follow_up';
  intent_analysis?: string;
  key_concerns?: string[];
  suggested_action?: string;
  confidence?: number;
}

// 分析回复响应（后端返回的消息格式）
export interface AnalyzeReplyResponse {
  message: string;
}

// 生成跟进内容响应（后端返回的消息格式）
export interface GenerateFollowUpResponse {
  message: string;
}

// 生成跟进内容请求
export interface GenerateFollowUpRequest {
  tone?: 'professional' | 'friendly' | 'casual';
  include_details?: boolean;
}

// 跟进内容响应
export interface FollowUpContent {
  subject?: string;
  body: string;
  personalization_elements?: Array<{
    type: string;
    content: string;
  }>;
  word_count?: number;
  language?: string;
}

// 更新跟进内容请求
export interface UpdateFollowUpRequest {
  content: string;
}

// 邮箱账号（渠道账号）
export interface EmailAccount {
  id: string;
  tenant_id?: string;
  channel_type: ChannelType;
  sender_name?: string;
  sender_address: string;
  status: 'active' | 'inactive' | 'error';
  is_default: boolean;
  config?: any;
  last_used_at?: string;
  created_at: string;
  updated_at?: string;
  created_by?: string;
}

// 添加邮箱账号请求（渠道账号）
export interface CreateEmailAccountRequest {
  channel_type: ChannelType;
  sender_name?: string;
  sender_address: string;
  config: Record<string, any>;
  is_default?: boolean;
}

// 更新邮箱账号请求
export interface UpdateEmailAccountRequest {
  sender_name?: string;
  sender_address?: string;
  config?: Record<string, any>;
  status?: string;
  is_default?: boolean;
}

// 活动统计
export interface CampaignStats {
  total_sent: number;
  delivered: number;
  opened: number;
  replied: number;
  failed: number;
  open_rate: number;
  reply_rate: number;
  bounce_rate: number;
}

// 邀约记录
export interface OutreachRecord {
  id: string;
  influencer_id: string;
  influencer_name: string;
  email: string;
  platform: string;
  status: 'pending' | 'sent' | 'delivered' | 'opened' | 'replied' | 'bounced' | 'failed';
  sent_at?: string;
  opened_at?: string;
  replied_at?: string;
  created_at: string;
}
